<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯會員' : '新增會員' }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleCancel" :disabled="loading">取消</button>
        <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="loading || (isWixtarClient && isEdit)">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="isWixtarClient && isEdit" class="alert alert-warning wixtar-readonly-banner" role="alert">
        <strong>Wixtar 會員系統：</strong>會員主檔由 Wixtar 管理，無法在此編輯（含重設密碼）。請至 Wixtar 操作；CRM 僅供檢視同步後資料。
      </div>
      <div v-else-if="isWixtarClient && !isEdit" class="alert alert-info wixtar-readonly-banner" role="status">
        <strong>Wixtar 會員系統：</strong>將先在 Wixtar 建立會員，成功後再同步至本系統。Webhook 晚到只會更新既有鏡像，不會造成遞迴寫回。
      </div>
      <form @submit.prevent="handleSubmit">
        <div class="form-card">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="name" class="required">姓名</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                placeholder="請輸入姓名"
                required
                maxlength="50"
                :disabled="isWixtarClient && isEdit"
              />
              <div v-if="errors.name" class="invalid-feedback">
                {{ errors.name }}
              </div>
            </div>

            <div class="form-group">
              <label for="phone" class="required">手機號碼</label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="form-control"
                :class="{ 'is-invalid': errors.phone }"
                placeholder="請輸入手機號碼（09xxxxxxxx）"
                required
                pattern="^09[0-9]{8}$"
                :disabled="isEdit"
              />
              <div v-if="errors.phone" class="invalid-feedback">
                {{ errors.phone }}
              </div>
              <p v-if="isEdit" class="form-hint">手機號碼無法修改</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                placeholder="請輸入 Email"
                maxlength="100"
                :disabled="isWixtarClient && isEdit"
              />
              <div v-if="errors.email" class="invalid-feedback">
                {{ errors.email }}
              </div>
            </div>

            <div class="form-group">
              <label for="birthday">生日</label>
              <input
                id="birthday"
                v-model="form.birthday"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.birthday }"
                :max="maxDate"
                :disabled="isWixtarClient && isEdit"
              />
              <div v-if="errors.birthday" class="invalid-feedback">
                {{ errors.birthday }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="gender">性別</label>
              <select
                id="gender"
                v-model="form.gender"
                class="form-control"
                :disabled="isWixtarClient && isEdit"
              >
                <option value="">請選擇</option>
                <option value="male">男</option>
                <option value="female">女</option>
                <option value="neutral">中性</option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-card">
          <h3 class="section-title">地址資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="address_city">城市</label>
              <select
                id="address_city"
                v-model="form.address_city"
                class="form-control"
                :disabled="isWixtarClient && isEdit"
                @change="handleCityChange"
              >
                <option value="">請選擇城市</option>
                <option 
                  v-for="city in cities" 
                  :key="city.id" 
                  :value="city.cityName"
                  :data-city-id="city.id"
                  :data-city-code="city.cityCode"
                >
                  {{ city.cityName }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="address_district">區域</label>
              <select
                id="address_district"
                v-model="form.address_district"
                class="form-control"
                :disabled="(isWixtarClient && isEdit) || !selectedCityId || districts.length === 0"
                @change="handleDistrictChange"
              >
                <option value="">請選擇區域</option>
                <option 
                  v-for="district in districts" 
                  :key="district.id" 
                  :value="district.districtName"
                  :data-postal-code="district.postalCode"
                >
                  {{ district.districtName }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="address_street">街道地址</label>
              <input
                id="address_street"
                v-model="form.address_street"
                type="text"
                class="form-control"
                placeholder="請輸入街道地址"
                maxlength="255"
                :disabled="isWixtarClient && isEdit"
              />
            </div>

            <div class="form-group">
              <label for="address_postal_code">郵遞區號</label>
              <input
                id="address_postal_code"
                v-model="form.address_postal_code"
                type="text"
                class="form-control"
                placeholder="請輸入郵遞區號"
                maxlength="10"
                :disabled="isWixtarClient && isEdit"
              />
            </div>
          </div>
        </div>

        <div class="form-card">
          <h3 class="section-title">會員設定</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="member_tier">會員等級</label>
              <select
                id="member_tier"
                v-model="form.member_tier"
                class="form-control"
                :disabled="isWixtarClient && isEdit"
              >
                <option value="">請選擇</option>
                <option value="regular">一般會員</option>
                <option value="silver">銀卡會員</option>
                <option value="gold">金卡會員</option>
                <option value="platinum">白金會員</option>
              </select>
            </div>

            <div class="form-group">
              <label for="status">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
                :disabled="isWixtarClient && isEdit"
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
                <option value="pending">待審核</option>
                <option value="blocked">已封鎖</option>
              </select>
            </div>
          </div>
        </div>

        <div v-if="isEdit && !isWixtarClient" class="form-card">
          <h3 class="section-title">登入密碼（選填）</h3>
          <p class="form-hint password-section-hint">
            若會員使用手機與密碼登入，可在此重設密碼。留空則維持原密碼不變。
          </p>
          <div class="form-row">
            <div class="form-group">
              <label for="reset_password_new">新密碼</label>
              <input
                id="reset_password_new"
                v-model="passwordReset.newPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.resetPassword }"
                autocomplete="new-password"
                placeholder="至少 8 個字元，留空則不變更"
                minlength="8"
              />
            </div>
            <div class="form-group">
              <label for="reset_password_confirm">確認新密碼</label>
              <input
                id="reset_password_confirm"
                v-model="passwordReset.confirmPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.resetPassword }"
                autocomplete="new-password"
                placeholder="再次輸入新密碼"
                minlength="8"
              />
            </div>
          </div>
          <div v-if="errors.resetPassword" class="invalid-feedback d-block">
            {{ errors.resetPassword }}
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { memberService } from '@/api/modules/members.js';
import { cityService } from '@/api/modules/cities.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { memberNameError } from '../../../../../../shared/memberName.js';

export default {
  name: 'CreateMember',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});
    const cities = ref([]);
    const districts = ref([]);
    const selectedCityId = ref(null);
    const loadingCities = ref(false);
    const loadingDistricts = ref(false);
    const isWixtarClient = ref(false);

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    const maxDate = computed(() => {
      const today = new Date();
      return today.toISOString().split('T')[0];
    });

    const form = reactive({
      name: '',
      phone: '',
      email: '',
      birthday: '',
      gender: '',
      address_city: '',
      address_district: '',
      address_street: '',
      address_postal_code: '',
      member_tier: '',
      status: 'active',
    });

    const passwordReset = reactive({
      newPassword: '',
      confirmPassword: '',
    });

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        }
      } catch (e) {
        console.error('[CreateMember] 無法載入客戶會員系統類型', e);
      }
    };

    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      if (isWixtarClient.value && isEdit.value) {
        return false;
      }

      // 驗證姓名
      const nameMessage = memberNameError(form.name);
      if (!form.name || !form.name.trim()) {
        errors.name = '請填寫姓名';
        isValid = false;
      } else if (nameMessage) {
        errors.name = nameMessage;
        isValid = false;
      }

      // 驗證手機號碼
      const phoneRegex = /^09[0-9]{8}$/;
      if (!form.phone || !phoneRegex.test(form.phone)) {
        errors.phone = '請輸入有效的手機號碼（09xxxxxxxx）';
        isValid = false;
      }

      // 驗證 Email（選填，但如果填了就要驗證格式）
      if (form.email && form.email.trim().length > 0) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
          errors.email = '請輸入有效的 Email 地址';
          isValid = false;
        }
      }

      // 驗證生日（如果填了，必須是過去的日期）
      if (form.birthday) {
        const birthday = new Date(form.birthday);
        const today = new Date();
        if (birthday >= today) {
          errors.birthday = '生日必須是過去的日期';
          isValid = false;
        }
      }

      if (isEdit.value) {
        const np = (passwordReset.newPassword || '').trim();
        const cp = (passwordReset.confirmPassword || '').trim();
        if (np || cp) {
          if (!np || !cp) {
            errors.resetPassword = '請同時填寫新密碼與確認密碼';
            isValid = false;
          } else if (np.length < 8) {
            errors.resetPassword = '新密碼至少 8 個字元';
            isValid = false;
          } else if (np !== cp) {
            errors.resetPassword = '兩次輸入的密碼不一致';
            isValid = false;
          }
        }
      }

      return isValid;
    };

    /** 後端 PUT/PATCH 的 JSON Schema 會驗證每個送出欄位；編輯時手機不可改，若 DB 為舊格式勿再送 phone 以免 400 */
    const buildSubmitData = () => {
      const data = {};
      const setIfPresent = (key, val) => {
        if (val !== '' && val !== null && val !== undefined) {
          data[key] = val;
        }
      };

      setIfPresent('name', typeof form.name === 'string' ? form.name.trim() : form.name);
      if (!isEdit.value) {
        setIfPresent('phone', typeof form.phone === 'string' ? form.phone.trim() : form.phone);
      }
      setIfPresent('email', typeof form.email === 'string' ? form.email.trim() : form.email);
      setIfPresent('birthday', form.birthday);
      data.gender = ['male', 'female', 'neutral'].includes(form.gender) ? form.gender : null;
      setIfPresent('address_city', form.address_city);
      setIfPresent('address_district', form.address_district);
      setIfPresent('address_street', form.address_street);
      setIfPresent('address_postal_code', form.address_postal_code);
      setIfPresent('member_tier', form.member_tier);
      setIfPresent('status', form.status);

      return data;
    };

    const loadMember = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      errorMessage.value = '';

      try {
        console.log('[CreateMember] Loading member:', route.params.id);
        const response = await memberService.getMember(route.params.id);
        
        console.log('[CreateMember] API Response:', response);

        if (response && response.success && response.data) {
          const member = response.data;
          console.log('[CreateMember] Member data:', member);
          form.name = member.name || '';
          form.phone = member.phone || '';
          form.email = member.email || '';
          form.birthday = member.birthday ? member.birthday.split('T')[0] : '';
          form.gender = ['male', 'female', 'neutral'].includes(member.gender) ? member.gender : '';
          form.address_city = member.addressCity || member.address_city || '';
          form.address_district = member.addressDistrict || member.address_district || '';
          form.address_street = member.addressStreet || member.address_street || '';
          form.address_postal_code = member.addressPostalCode || member.address_postal_code || '';
          form.member_tier = member.memberTier || member.member_tier || member.tier || '';
          form.status = member.status || 'active';
          if (member.memberSystemType === 'WIXTAR' || member.member_system_type === 'WIXTAR') {
            isWixtarClient.value = true;
          }
        } else {
          const errorMsg = response?.message || (response?.error && response.error.message) || '獲取會員資料失敗';
          console.error('[CreateMember] API Error:', {
            response,
            errorMsg,
            success: response?.success,
            data: response?.data,
            message: response?.message,
            error: response?.error
          });
          errorMessage.value = errorMsg;
          await showError('載入失敗', errorMsg);
        }
      } catch (err) {
        console.error('[CreateMember] Exception:', {
          error: err,
          message: err.message,
          response: err.response,
          data: err.data,
          code: err.code,
          details: err.details
        });
        errorMessage.value = err.message || err.data?.message || '獲取會員資料失敗';
        await showError('載入失敗', errorMessage.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (isWixtarClient.value && isEdit.value) {
        await showError('無法儲存', '此客戶使用 Wixtar 會員系統，會員資料請至 Wixtar 更新。');
        return;
      }

      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const submitData = buildSubmitData();

        console.log('[CreateMember] Submitting data:', {
          isEdit: isEdit.value,
          memberId: route.params.id,
          submitData
        });

        let response;
        try {
          if (isEdit.value) {
            response = await memberService.updateMember(route.params.id, submitData);
          } else {
            response = await memberService.createMember(submitData);
          }

          console.log('[CreateMember] API Response:', response);
          console.log('[CreateMember] Response type:', typeof response);
          console.log('[CreateMember] Response keys:', response ? Object.keys(response) : 'null/undefined');

          // 檢查回應是否有效
          if (!response || typeof response !== 'object' || Object.keys(response).length === 0) {
            throw new Error('API 回應格式錯誤：收到空回應');
          }

          if (response && response.success) {
            const displayName = form.name || form.phone;
            if (isEdit.value) {
              const np = (passwordReset.newPassword || '').trim();
              const cp = (passwordReset.confirmPassword || '').trim();
              if (np && cp) {
                try {
                  const pr = await memberService.resetMemberPassword(route.params.id, {
                    newPassword: np,
                    confirmPassword: cp,
                  });
                  if (!pr || !pr.success) {
                    const msg = pr?.message || pr?.error?.message || '重設密碼失敗';
                    errorMessage.value = `會員資料已更新，但${msg}`;
                    await showError('密碼重設失敗', msg);
                    loading.value = false;
                    return;
                  }
                } catch (pwdErr) {
                  const msg =
                    pwdErr?.message ||
                    pwdErr?.response?.data?.message ||
                    pwdErr?.response?.data?.error?.message ||
                    '重設密碼失敗';
                  errorMessage.value = `會員資料已更新，但${msg}`;
                  await showError('密碼重設失敗', msg);
                  loading.value = false;
                  return;
                }
              }
            }
            passwordReset.newPassword = '';
            passwordReset.confirmPassword = '';
            await showSuccess(
              isEdit.value ? '更新成功' : '建立成功',
              `會員「${displayName}」已成功${isEdit.value ? '更新' : '建立'}`
            );
            router.push('/client/members');
          } else {
            // 如果 success 為 false，但沒有拋出錯誤，可能是業務邏輯錯誤
            const errorMsg = response?.message || (response?.error && response.error.message) || `${isEdit.value ? '更新' : '建立'}會員失敗`;
            console.error('[CreateMember] API Error (success=false):', {
              response,
              errorMsg,
              success: response?.success,
              data: response?.data,
              message: response?.message,
              error: response?.error
            });
            errorMessage.value = errorMsg;
            await showError(isEdit.value ? '更新失敗' : '建立失敗', errorMsg);
          }
        } catch (apiError) {
          // 如果 API 調用本身失敗（網路錯誤、超時等），重新拋出讓外層 catch 處理
          console.error('[CreateMember] API call failed:', apiError);
          throw apiError;
        }
      } catch (err) {
        console.error('[CreateMember] Exception:', {
          error: err,
          message: err.message,
          response: err.response,
          data: err.response?.data,
          status: err.response?.status,
          code: err.code,
          details: err.details
        });
        
        // 從 API 回應中提取錯誤訊息
        let errorMsg = `${isEdit.value ? '更新' : '建立'}會員失敗`;
        
        // 優先使用 err.message（axios interceptor 已經處理過）
        if (err.message) {
          errorMsg = err.message;
        }
        
        // 如果沒有，從 response.data 中提取
        if (err.response?.data) {
          const errorData = err.response.data;
          
          // 優先使用頂層的 message
          if (errorData.message) {
            errorMsg = errorData.message;
          } 
          // 其次使用 error.message
          else if (errorData.error?.message) {
            errorMsg = errorData.error.message;
          }
          
          // 如果是 409 衝突錯誤（手機號碼已註冊），顯示更友好的訊息
          if (err.response.status === 409 || err.code === 'CONFLICT') {
            if (errorData.details?.field === 'phone') {
              errorMsg = `手機號碼 ${errorData.details.value || ''} 已被註冊，請使用其他手機號碼`;
            } else if (errorData.message) {
              errorMsg = errorData.message;
            }
          }
          
          // 如果有詳細的驗證錯誤，顯示第一個
          if (errorData.error?.details) {
            const details = errorData.error.details;
            if (typeof details === 'object') {
              const firstError = Object.values(details)[0];
              if (firstError && !errorMsg.includes('手機號碼')) {
                errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
              }
            }
          }
        }
        
        errorMessage.value = errorMsg;
        
        // 根據錯誤類型顯示不同的標題
        let errorTitle = isEdit.value ? '更新失敗' : '建立失敗';
        if (err.response?.status === 409 || err.code === 'CONFLICT') {
          errorTitle = '手機號碼已註冊';
        }
        
        await showError(errorTitle, errorMsg);
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/members');
    };

    // 載入城市列表
    const loadCities = async () => {
      loadingCities.value = true;
      try {
        const response = await cityService.getCities();
        if (response && response.success && response.data) {
          cities.value = response.data;
        }
      } catch (err) {
        console.error('載入城市列表失敗:', err);
      } finally {
        loadingCities.value = false;
      }
    };

    // 載入區域列表
    const loadDistricts = async (cityId) => {
      if (!cityId) {
        districts.value = [];
        return;
      }

      loadingDistricts.value = true;
      try {
        const response = await cityService.getDistrictsByCityId(cityId);
        if (response && response.success && response.data) {
          districts.value = response.data;
        }
      } catch (err) {
        console.error('載入區域列表失敗:', err);
        districts.value = [];
      } finally {
        loadingDistricts.value = false;
      }
    };

    // 處理城市變更
    const handleCityChange = (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      const cityId = selectedOption.getAttribute('data-city-id');
      const cityCode = selectedOption.getAttribute('data-city-code');
      
      selectedCityId.value = cityId;
      form.address_city = selectedOption.value;
      
      // 清空區域和郵遞區號
      form.address_district = '';
      form.address_postal_code = '';
      districts.value = [];
      
      // 載入對應的區域
      if (cityId) {
        loadDistricts(cityId);
      }
    };

    // 處理區域變更
    const handleDistrictChange = (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      const postalCode = selectedOption.getAttribute('data-postal-code');
      
      form.address_district = selectedOption.value;
      
      // 自動填入郵遞區號
      if (postalCode) {
        form.address_postal_code = postalCode;
      }
    };

    // 根據城市名稱查找城市 ID（用於編輯時載入區域）
    const findCityIdByName = (cityName) => {
      const city = cities.value.find(c => c.cityName === cityName);
      return city ? city.id : null;
    };

    onMounted(async () => {
      await Promise.all([loadClientMemberSystem(), loadCities()]);
      await loadMember();
      
      // 如果是在編輯模式且已有城市資料，載入對應的區域
      if (isEdit.value && form.address_city) {
        const cityId = findCityIdByName(form.address_city);
        if (cityId) {
          selectedCityId.value = cityId;
          await loadDistricts(cityId);
          
          // 如果已有區域，確保選中正確的區域
          if (form.address_district) {
            // 區域選單會自動顯示已選中的值
          }
        }
      }
    });

    return {
      form,
      passwordReset,
      errors,
      loading,
      errorMessage,
      isWixtarClient,
      isEdit,
      maxDate,
      cities,
      districts,
      selectedCityId,
      loadingCities,
      loadingDistricts,
      handleSubmit,
      handleCancel,
      handleCityChange,
      handleDistrictChange,
    };
  },
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.page-title { font-size: 20px; font-weight: 600; margin: 0; }

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
}

.form-card:last-of-type { margin-bottom: 0; }

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.form-row:last-child { margin-bottom: 0; }

.form-group { display: flex; flex-direction: column; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.is-invalid { border-color: #ef4444; }
.form-control:disabled { background-color: #f9fafb; cursor: not-allowed; color: #9ca3af; }

.invalid-feedback { color: #ef4444; font-size: 12px; margin-top: 4px; }
.form-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; margin-bottom: 0; }
.password-section-hint { margin-bottom: 16px; }
.invalid-feedback.d-block { margin-top: 0; }

.alert { padding: 12px 16px; border-radius: 6px; margin-top: 16px; }
.alert-danger { background-color: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.wixtar-readonly-banner {
  margin-bottom: 16px;
  background-color: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
